/**
 * useDevices — datalaget for innrullerte kjøkkenskjermer (#142).
 *
 *   - useAdminDevices()   GET  /api/devices             (requireAdminScope)
 *   - useEnrollDevice()   POST /api/devices/enroll      → setter `stell_device`
 *   - useRevokeDevice()   POST /api/devices/:id/revoke
 *
 * Mutasjonene kaster `ApiError`, så seksjonen kan åpne adminopplåsingen ved
 * en 403 og prøve igjen — samme mønster som useMembers.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DeviceEnrollInput, DeviceRow } from '@stell/shared';
import { useSession } from '../auth/useSession';
import { toApiError } from './useMembers';

export const ADMIN_DEVICES_KEY = ['admin', 'devices'] as const;

export function useAdminDevices() {
  const { data: session } = useSession();
  return useQuery<DeviceRow[]>({
    queryKey: ADMIN_DEVICES_KEY,
    enabled: session?.member?.role === 'admin',
    queryFn: async () => {
      const res = await fetch('/api/devices', { credentials: 'same-origin' });
      if (!res.ok) throw await toApiError(res);
      return ((await res.json()) as { devices: DeviceRow[] }).devices;
    },
  });
}

export function useEnrollDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: DeviceEnrollInput): Promise<DeviceRow> => {
      const res = await fetch('/api/devices/enroll', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await toApiError(res);
      return (await res.json()) as DeviceRow;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ADMIN_DEVICES_KEY }),
  });
}

export function useRevokeDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await fetch(`/api/devices/${encodeURIComponent(id)}/revoke`, {
        method: 'POST',
        credentials: 'same-origin',
      });
      if (!res.ok) throw await toApiError(res);
    },
    // Tilbakekalling kan ha avsluttet økten på denne nettleseren (hvis det var
    // denne enheten), så alt hentes på nytt — også ['session'].
    onSuccess: () => qc.invalidateQueries(),
  });
}
